// @flow

/**
 * Prompt Button Component
 * Renders a <prompt> tag as a clickable chip/pill button
 */

import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';
import { useCustomTags } from '../context';

const PromptChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  margin: 0.15rem 0.15rem 0.15rem 0;
  border: 1px solid var(--primary, #137cbd);
  border-radius: 1rem;
  background: var(--primary-light, rgba(19, 124, 189, 0.1));
  color: var(--primary, #137cbd);
  font-family: var(--fonts-default);
  font-size: 0.875rem;
  line-height: 1.3;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  white-space: normal;
  text-align: left;

  &:hover:not(:disabled) {
    background: var(--primary, #137cbd);
    color: white;
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type Props = {
  children?: React$Node,
};

const PromptButton = ({ children }: Props): React$Node => {
  const customTagsContext = useCustomTags();
  const submitPrompt = customTagsContext?.submitPrompt;
  const isStreaming = customTagsContext?.isStreaming ?? false;
  const promptButtonsDisabled = customTagsContext?.promptButtonsDisabled ?? false;

  // Extract text content from children
  const promptText = useMemo(() => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.join('');
    return String(children || '');
  }, [children]);

  const isEmpty = !promptText.trim();
  const isDisabled = isStreaming || promptButtonsDisabled || isEmpty || !submitPrompt;

  const handleClick = useCallback(() => {
    if (!isDisabled && submitPrompt) {
      submitPrompt(promptText);
    }
  }, [isDisabled, submitPrompt, promptText]);

  return (
    <PromptChip
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      title={isEmpty ? 'Empty prompt' : promptText}
    >
      {promptText || '(empty)'}
      <Icon icon="arrow-right" iconSize={12} />
    </PromptChip>
  );
};

export default PromptButton;
